import { getGrocery } from "@/services";
import useReq from "@/utils/useReq";
import { Button, Tooltip } from "antd";
import { isBoolean } from "lodash";
import { useMemo } from "react";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import styles from "./index.module.less";

interface AiInfo {
  gd_path: string;
  prompt: string;
  aigc_category: string;
  model: string;
  extra?: {
    seed?: string | number;
    step?: string | number;
    scale?: string | number;
    sampler?: string;
    negativePrompt?: string;
    width?: string | number;
    height?: string | number;
    ai_project?: string;
    style_code?: string;
    controlnet?: {
      name: string;
      model: string;
      weight: string;
      preprocessor: string;
    };
  };
}

function isGdAi(aigc_category: string) {
  return aigc_category === "gaoding_ai" || aigc_category === "gd_ai";
}

export default function PinAIInfo({
  info,
  pin,
}: {
  info: AiInfo;
  pin: { file: { width: number; height: number } };
}) {
  const onCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    HBMessage.success("复制成功");
  };

  const { data: aiParamsConf } = useReq(
    () => getGrocery<GroceryConfig.AiParamsConf>("pin_detail_ai_params"),
    {
      refreshDeps: [info],
    }
  );

  const aigcLink = useMemo(() => {
    if (!aiParamsConf) {
      return <span>{info.aigc_category}</span>;
    }
    const option = aiParamsConf?.linkMap?.find(
      (v) => v.value === info.aigc_category
    );

    const aigcLink = option?.link ? (
      <a
        href={option.link}
        className={styles.aigcLink}
        target="_blank"
        rel="nofollow noreferrer"
        data-gd-click="button_click"
        data-button-name={`创作工具_${option.name}`}
      >
        {option.name}
        <HBIcon type="launch" className={styles.aigcLinkIcon} />
      </a>
    ) : (
      <span>{option?.name || "其他"}</span>
    );

    return aigcLink;
  }, [aiParamsConf, info.aigc_category]);

  const size = useMemo(() => {
    const { width, height } = info?.extra || {};
    if (width && height) {
      return `${width} x ${height}`;
    }
    return `${pin.file.width} x ${pin.file.height}`;
  }, [info.extra, pin.file.height, pin.file.width]);

  const env = process.env.NEXT_PUBLIC_DEPLOY_ENV || "prod";

  const gaodingPath = useMemo(() => {
    return `https://${env === "prod" ? "" : env + "."}gaoding.com${
      info.gd_path
    }`;
  }, [env, info.gd_path]);

  const pluginProperty = useMemo(() => {
    if (!info.extra?.controlnet || !aiParamsConf?.plugin) {
      return null;
    }
    const object = info.extra?.controlnet || {};

    let propertys = [];
    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        const value = object[key];
        const plugin = aiParamsConf?.plugin?.find((v) => v.key === key);
        if (plugin) {
          const text = isBoolean(value) ? (value ? "开启" : "关闭") : value;
          propertys.push(
            <div className={styles.pluginProperty} key={key}>
              <span>{plugin.name}：</span> <span>{text}</span>
            </div>
          );
        }
      }
    }

    return (
      <div className={styles.pluginInfo}>
        <div className={styles.pluginName}>{info?.extra?.controlnet?.name}</div>
        {propertys}
      </div>
    );
  }, [aiParamsConf?.plugin, info.extra?.controlnet]);

  return (
    <div className={styles.aiInfo}>
      {info.prompt && (
        <>
          <div className={styles.title}>
            提示词（Prompt）
            <HBIcon
              className={styles.right}
              type="copy"
              onClick={() => onCopy(info.prompt)}
              data-tooltip={"复制"}
              data-tooltip-position="bottom"
              data-gd-click="button_click"
              data-button-name="复制Prompt"
            />
          </div>
          <div className={styles.copyContent} title={info.prompt}>
            <div>{info.prompt}</div>
          </div>
        </>
      )}

      {info.extra?.negativePrompt && (
        <>
          <div className={styles.title}>
            反向提示词（Negative Prompt）
            <HBIcon
              className={styles.right}
              type="copy"
              data-tooltip={"复制"}
              data-tooltip-position="bottom"
              onClick={() => onCopy(info.extra.negativePrompt)}
              data-gd-click="button_click"
              data-button-name="复制NegativePrompt"
            />
          </div>
          <div className={styles.copyContent} title={info.extra.negativePrompt}>
            <div>{info.extra.negativePrompt}</div>
          </div>
        </>
      )}

      <div className={styles.title}>
        创作信息
        {aiParamsConf?.aigcDescHref && (
          <Tooltip
            placement="right"
            title={
              <a
                className={styles.tooltipLink}
                href={aiParamsConf?.aigcDescHref}
                target="_blank"
                rel="nofollow noreferrer"
                data-gd-click="button_click"
                data-button-name="AIGC说明书"
              >
                AIGC说明书
              </a>
            }
          >
            <HBIcon type="hep" />
          </Tooltip>
        )}
      </div>
      <div className={styles.property}>
        <div className={styles.item}>
          <span className={styles.label}>创作工具</span>
          <span className={styles.value}>{aigcLink}</span>
        </div>
        <div className={styles.item}>
          <span className={styles.label}>模型版本</span>
          <span className={styles.value}>
            {info.model === "稿定AI" && aiParamsConf?.hideGdBtn
              ? "其他"
              : info.model || "未知"}
          </span>
        </div>
        <div className={styles.item}>
          <span className={styles.label}>图片尺寸</span>
          <span className={styles.value}>{size}</span>
        </div>
        {pluginProperty && (
          <div className={styles.item}>
            <span className={styles.label}>插件信息</span>
            <span className={styles.value}>
              <Tooltip placement="bottom" title={pluginProperty}>
                <span className={styles.pluginValue}>
                  ControlNet <HBIcon type="ic_info1" />
                </span>
              </Tooltip>
            </span>
          </div>
        )}
      </div>
      <div className={styles.tags}>
        {aiParamsConf &&
          info.extra &&
          aiParamsConf.aiParam.map(({ name, key }) => {
            const value = info.extra[key] || info[key];
            if (info.extra[key] || info[key]) {
              return (
                <span key={key}>
                  {name}：{value}
                </span>
              );
            }
            return null;
          })}
      </div>
      {!aiParamsConf?.hideGdBtn && isGdAi(info.aigc_category) && (
        <Button
          type="text"
          className={styles.btn}
          href={gaodingPath}
          target="_blank"
          data-gd-click="button_click"
          data-button-name="稿定AI创作同款"
        >
          去稿定AI创作同款 <HBIcon type="ic_open" />
        </Button>
      )}
    </div>
  );
}
